import { getAuth } from 'firebase/auth';

interface GenerateHeadshotParams {
  instances: Array<{
    prompt: string;
    image_bytes: {
      image_bytes: string;
    };
  }>;
  parameters: {
    sampleCount: number;
  };
}

interface GenerateHeadshotResponse {
  imageData: string;
}

/**
 * Firebase Functions経由でImagen 3.0 Edit APIを使用して宣材写真を生成します。
 * @param base64Image ソース画像のbase64データURL。
 * @param mimeType ソース画像のMIMEタイプ。
 * @param prompt AIへの詳細なテキストプロンプト。
 * @param customOutfitImage オプションの衣装画像のbase64データとMIMEタイプ。
 * @param customBackgroundImage オプションの背景画像のbase64データとMIMEタイプ。
 * @returns 生成された画像のbase64データURL、または画像が返されなかった場合はnull。
 */
export const generateHeadshot = async (
  base64Image: string,
  mimeType: string,
  prompt: string,
  customOutfitImage: { data: string; mimeType: string; } | null,
  customBackgroundImage: { data: string; mimeType: string; } | null
): Promise<string | null> => {
  try {
    if (!base64Image) {
      throw new Error('画像データが存在しません。画像をアップロードしてください。');
    }

    // Base64プレフィックスを除去
    const cleanBase64 = base64Image.replace(/^data:image\/(.+);base64,/, '');

    // Firebase Auth tokenを取得（無料トライアルユーザーは認証なし）
    const auth = getAuth();
    const user = auth.currentUser;
    let token = null;
    
    if (user) {
      token = await user.getIdToken();
    }

    // Firebase Callable Function用のデータ準備

    // Firebase Callable Functionを使用
    const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import('firebase/functions');
    const { getApp } = await import('firebase/app');
    
    // Firebase Appインスタンスを取得
    const firebaseApp = getApp();
    const functions = getFunctions(firebaseApp, 'us-central1');
    
    // 環境変数でエミュレーター接続を制御
    // VITE_USE_FUNCTIONS_EMULATOR=true の場合のみエミュレーターに接続
    const useEmulator = import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true';
    const isLocalhost = typeof window !== 'undefined' && 
                       (window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1');
    
    if (useEmulator && isLocalhost) {
      try {
        connectFunctionsEmulator(functions, 'localhost', 5001);
        console.log('✅ Functions Emulatorに接続しました: localhost:5001');
      } catch (error: any) {
        // 既に接続されている場合は無視
        if (!error.message?.includes('already been called') && 
            !error.message?.includes('already connected')) {
          console.warn('⚠️ Functions Emulator接続エラー（本番環境に接続します）:', error.message);
        }
      }
    } else {
      console.log('📡 本番環境のFirebase Functionsに接続します');
    }
    
    const generateHeadshot = httpsCallable(functions, 'generateHeadshotV1');

    const requestData = {
      base64Image: base64Image,
      mimeType: mimeType,
      prompt: prompt,
      customOutfitImage: customOutfitImage,
      customBackgroundImage: customBackgroundImage
    };

    // モバイル環境でのタイムアウトを長めに設定（90秒）
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const timeoutMs = isMobile ? 90000 : 60000; // モバイル: 90秒、PC: 60秒
    
    let timeoutId: NodeJS.Timeout | null = null;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error('画像生成がタイムアウトしました。しばらく待ってから再度お試しください。'));
      }, timeoutMs);
    });

    try {
      const result = await Promise.race([
        generateHeadshot(requestData),
        timeoutPromise
      ]);

      // 成功した場合はタイマーをクリーンアップ
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (!result.data.imageData) {
        throw new Error('画像生成に失敗しました');
      }

      return result.data.imageData;
    } catch (error) {
      // エラー時もタイマーをクリーンアップ
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      throw error;
    }

  } catch (error: any) {
    console.error('❌ 画像生成エラー:', error);
    console.error('エラー詳細:', {
      code: error.code,
      message: error.message,
      details: error.details
    });

    // 接続エラーの場合
    if (error.code === 'unavailable' || 
        error.message?.includes('CONNECTION_REFUSED') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError')) {
      throw new Error('サーバーに接続できませんでした。エミュレーターが起動しているか確認してください。');
    }

    if (error.code === 'unauthenticated') {
      throw new Error('ログインが必要です');
    }

    // Firebase Functionsのエラーコードを処理
    if (error.code === 'deadline-exceeded') {
      throw new Error('生成に時間がかかりすぎてしまいました。リトライしてください。');
    }

    if (error.code === 'invalid-argument') {
      throw new Error(error.message || '入力内容に問題があります。内容を確認してお試しください。');
    }

    if (error.code === 'internal') {
      throw new Error(error.message || 'サーバーエラーが発生しました。しばらく時間をおいて再度お試しください。');
    }

    if (error.message?.includes('SAFETY') || error.message?.includes('safety')) {
      throw new Error("安全フィルターにより生成が拒否されました。内容を変更してお試しください。");
    } else if (error.message?.includes('BLOCKED') || error.message?.includes('blocked')) {
      throw new Error("不適切なコンテンツが検出されました。内容を変更してお試しください。");
    } else if (error.message?.includes('QUOTA') || error.message?.includes('quota')) {
      throw new Error("API使用量の上限に達しました。しばらく時間をおいてからお試しください。");
    } else if (error.message?.includes('INVALID_ARGUMENT')) {
      throw new Error("入力内容に問題があります。内容を確認してお試しください。");
    }

    throw new Error(error.message || "画像生成に失敗しました。しばらく時間をおいて再度お試しください。");
  }
};